<template>
  <section class="font-sans text-slate-900 dark:text-slate-100 bg-transparent">
    <!-- Loading overlay -->
    <AppLoader
      :isVisible="loading"
      variant="fullscreen"
      :message="loadingMessage || 'Processing Leave Request...'"
      subtext="Submitting and updating your institutional records"
      color="indigo"
      icon="sparkle"
    />

    <div class="space-y-4 sm:space-y-6">
      <!-- Toast Notifications -->
      <div class="fixed top-4 right-4 z-[10000] space-y-3">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="max-w-sm rounded-xl border p-3.5 shadow-xl backdrop-blur-sm transition-all duration-300 transform animate-in slide-in-from-right-full"
          :class="getToastClass(toast.type)"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-full grid place-items-center border bg-white dark:bg-[#111C30] shadow-xs"
              :class="getToastIconClass(toast.type)"
            >
              <CheckCircle2
                v-if="toast.type === 'success'"
                class="w-4 h-4 text-emerald-600"
              />
              <AlertCircle
                v-else
                class="w-4 h-4 text-rose-600"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p
                class="font-bold text-xs"
                :class="getToastTitleClass(toast.type)"
              >
                {{ getToastTitle(toast.type) }}
              </p>
              <p
                class="text-xs truncate"
                :class="getToastMessageClass(toast.type)"
              >
                {{ toast.message }}
              </p>
            </div>
            <button
              @click="removeToast(toast.id)"
              class="transition-colors p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/10"
              :class="getToastButtonClass(toast.type)"
            >
              <X class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Header Row with Title + New Request Button -->
      <div
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-5 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      >
        <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          <div
            class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0"
          >
            <CheckCircle class="w-5 h-5" />
          </div>
          <div class="min-w-0 flex-1">
            <h1
              class="text-sm sm:text-base md:text-lg font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate"
            >
              My Leave Applications
            </h1>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] truncate sm:whitespace-normal">
              Submit new leave requests and track previous applications
            </p>
          </div>
        </div>

        <button
          type="button"
          class="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm font-bold text-white px-4 py-2 sm:py-2.5 transition-all duration-200 shadow-xs hover:shadow-sm shrink-0"
          @click="openModal"
          :disabled="loading"
        >
          <Plus class="w-4 h-4" />
          <span>New Request</span>
        </button>
      </div>

      <!-- Filters Card -->
      <div class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl sm:rounded-2xl p-3.5 sm:p-4 shadow-xs">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
              <Filter class="w-4 h-4" />
            </div>
            <div>
              <h3 class="font-bold text-xs sm:text-sm text-slate-800 dark:text-slate-100">Filter History</h3>
              <p class="text-[11px] text-slate-400 dark:text-slate-500">Filter by reason, leave type, or period</p>
            </div>
          </div>

          <!-- Hamburger menu button for mobile -->
          <button
            type="button"
            @click="showMobileFilters = !showMobileFilters"
            class="w-8 h-8 md:hidden inline-flex items-center justify-center rounded-xl border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A] transition-colors shrink-0"
            :class="{ 'bg-slate-100 dark:bg-[#18243A]': showMobileFilters }"
            aria-label="Toggle mobile filters"
          >
            <Filter class="w-4 h-4 shrink-0" />
          </button>
        </div>

        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3 items-end transition-all duration-300"
          :class="{
            'hidden md:grid': !showMobileFilters,
            grid: showMobileFilters,
          }"
        >
          <div class="space-y-1">
            <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Search</label>
            <div class="relative">
              <Search
                class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                v-model="filters.search"
                type="text"
                class="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm rounded-xl bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 placeholder-slate-400 border border-slate-200/80 dark:border-[#263449] focus:bg-white dark:focus:bg-[#111C30] focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="Search by reason..."
                @keyup.enter="fetchLeaves"
              />
            </div>
          </div>

          <div class="space-y-1">
            <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Leave Type</label>
            <div class="relative">
              <select
                v-model="filters.leave_type"
                class="w-full px-3 py-2 text-xs sm:text-sm rounded-xl bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-[#263449] focus:bg-white dark:focus:bg-[#111C30] focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition appearance-none"
              >
                <option value="">All Types</option>
                <option
                  v-for="type in leaveTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>
              <ChevronDown
                class="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
          </div>

          <div class="space-y-1">
            <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Month</label>
            <div class="relative">
              <select
                v-model="filters.month"
                class="w-full px-3 py-2 text-xs sm:text-sm rounded-xl bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-[#263449] focus:bg-white dark:focus:bg-[#111C30] focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition appearance-none"
              >
                <option value="">All Months</option>
                <option
                  v-for="(month, index) in months"
                  :key="index"
                  :value="index + 1"
                >
                  {{ month }}
                </option>
              </select>
              <ChevronDown
                class="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
          </div>

          <div class="space-y-1">
            <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Year</label>
            <div class="relative">
              <select
                v-model="filters.year"
                class="w-full px-3 py-2 text-xs sm:text-sm rounded-xl bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-[#263449] focus:bg-white dark:focus:bg-[#111C30] focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition appearance-none"
              >
                <option
                  v-for="year in availableYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
              <ChevronDown
                class="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
          </div>

          <div class="flex gap-2">
            <button
              class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-700 dark:text-slate-300 px-3 py-2 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-[#152238] disabled:opacity-50 transition"
              @click="clearFilters"
              :disabled="loading"
            >
              <RotateCcw class="w-3.5 h-3.5" />
              Clear
            </button>
            <button
              class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-xs font-semibold disabled:opacity-50 transition shadow-xs"
              @click="fetchLeaves"
              :disabled="loading"
            >
              <Filter class="w-3.5 h-3.5" />
              {{ getFilterButtonText }}
            </button>
          </div>
        </div>
      </div>

      <!-- Leave Balance Cards (Only Non-Admin) -->
      <div
        v-if="userRole !== 'admin' && leaveBalance && leaveBalance.balances && leaveBalance.balances.length"
        class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4"
      >
        <div
          v-for="item in leaveBalance.balances"
          :key="item.type"
          class="rounded-xl border border-slate-200/90 dark:border-[#263449] bg-white dark:bg-[#111C30] p-3 sm:p-3.5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all"
        >
          <div class="flex justify-between items-center mb-1.5">
            <span class="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
              {{ item.type }}
            </span>
            <span
              class="text-[11px] font-bold px-1.5 py-0.5 rounded"
              :class="item.remaining <= 2 ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400' : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400'"
            >
              {{ item.remaining }} left
            </span>
          </div>

          <!-- Progress bar -->
          <div class="w-full bg-slate-100 dark:bg-[#1E293B] rounded-full h-1.5 my-1 overflow-hidden">
            <div
              class="h-full rounded-full bg-indigo-600 dark:bg-indigo-500 transition-all duration-300"
              :style="{
                width: item.total ? Math.min(100, (item.used / item.total) * 100) + '%' : '0%',
              }"
            ></div>
          </div>

          <div class="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
            Used: {{ item.used }} / {{ item.total }} days
          </div>
        </div>
      </div>

      <!-- Stats Cards (Matching RequestCards design system: 2 cols on mobile, 4 on desktop) -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4 mb-2 sm:mb-4">
        <!-- Total Applications -->
        <div
          class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
        >
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <div class="p-1.5 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-blue-600 dark:text-blue-400">
                <FileText class="w-4 h-4" />
              </div>
              <span
                class="text-[10px] font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded"
              >
                Total
              </span>
            </div>
            <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
              Total Applications
            </h3>
            <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
              {{ getTotalLeaves }}
            </p>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5 truncate">
            All submitted applications
          </p>
        </div>

        <!-- Pending Review -->
        <div
          class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
        >
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <div class="p-1.5 bg-amber-50 dark:bg-amber-950/40 rounded-lg text-amber-600 dark:text-amber-400">
                <Clock class="w-4 h-4" />
              </div>
              <span class="text-[10px] font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded">
                Pending
              </span>
            </div>
            <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
              Pending Applications
            </h3>
            <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
              {{ getTotalPending }}
            </p>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5 truncate">
            Awaiting admin decision
          </p>
        </div>

        <!-- Approved -->
        <div
          class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
        >
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <div class="p-1.5 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 class="w-4 h-4" />
              </div>
              <span class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded">
                Approved
              </span>
            </div>
            <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
              Approved Applications
            </h3>
            <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
              {{ getTotalApproved }}
            </p>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5 truncate">
            Confirmed and processed
          </p>
        </div>

        <!-- Rejected / Declined -->
        <div
          class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
        >
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <div class="p-1.5 bg-rose-50 dark:bg-rose-950/40 rounded-lg text-rose-600 dark:text-rose-400">
                <XCircle class="w-4 h-4" />
              </div>
              <span class="text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded">
                Declined
              </span>
            </div>
            <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
              Declined Applications
            </h3>
            <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
              {{ getTotalDeclined }}
            </p>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5 truncate">
            Rejected or non-approved
          </p>
        </div>
      </div>

      <!-- Mobile cards (lg:hidden) -->
      <ul class="grid gap-3 lg:hidden">
        <li
          v-for="leave in leaves"
          :key="leave._id"
          class="bg-white dark:bg-[#111C30] border rounded-2xl p-4 shadow-xs relative flex flex-col justify-between overflow-hidden"
          :class="[
            leave.status === 'Pending'
              ? 'border-amber-300/80 dark:border-amber-600/50'
              : leave.status === 'Approved'
              ? 'border-emerald-200/80 dark:border-emerald-700/50'
              : 'border-slate-200/90 dark:border-[#263449]',
          ]"
        >
          <div>
            <div class="flex items-center justify-between mb-2.5">
              <span
                class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold border shadow-xs"
                :class="badgeClass(leave.status)"
              >
                <span
                  v-if="leave.status === 'Pending'"
                  class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span
                  v-else-if="leave.status === 'Approved'"
                  class="w-1.5 h-1.5 rounded-full bg-emerald-500"
                ></span>
                <span
                  v-else
                  class="w-1.5 h-1.5 rounded-full bg-rose-500"
                ></span>
                {{ leave.status }}
              </span>

              <div class="relative">
                <button
                  class="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors"
                  @click="toggleRowMenu(leave._id)"
                  :disabled="loading"
                >
                  <MoreVertical class="w-4 h-4" />
                </button>
                <div
                  v-if="rowMenuId === leave._id"
                  class="absolute right-0 top-8 z-50 min-w-36 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] p-1.5 shadow-lg"
                >
                  <button
                    class="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-50 transition-colors"
                    @click="cancelLeaveRequest(leave._id)"
                    :disabled="leave.status !== 'Pending' || loading"
                  >
                    <Trash2 class="w-3.5 h-3.5" /> Cancel Request
                  </button>
                </div>
              </div>
            </div>

            <!-- Schedule Ribbon -->
            <div class="bg-slate-50/90 dark:bg-[#152238] rounded-xl p-3 border border-slate-200/60 dark:border-[#202E44] space-y-2 mb-2.5">
              <div class="flex items-center justify-between">
                <span class="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200">
                  <span class="w-2 h-2 rounded-full bg-indigo-500"></span>
                  {{ leave.leave_type }}
                </span>
                <span class="text-[11px] font-black bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-md">
                  {{ leave.duration_days }} {{ leave.duration_days > 1 ? 'Days' : 'Day' }}
                </span>
              </div>
              <div class="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 font-medium">
                <Calendar class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                <span class="truncate">{{ formatDateTime(leave.from_datetime) }} ({{ leave.start_time }}) → {{ formatDateTime(leave.to_datetime) }} ({{ leave.end_time }})</span>
              </div>
            </div>

            <!-- Reason Quote -->
            <div class="relative pl-3 border-l-2 border-indigo-200 dark:border-indigo-800/60">
              <span class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Reason</span>
              <p class="text-xs text-slate-700 dark:text-slate-200 italic line-clamp-2 mt-0.5" :title="leave.reason">
                "{{ leave.reason }}"
              </p>
            </div>
          </div>
        </li>

        <li
          v-if="leaves.length === 0 && !loading"
          class="rounded-xl border-2 border-dashed border-slate-200 dark:border-[#263449] p-8 text-center bg-white/40 dark:bg-[#111C30]/40"
        >
          <div class="flex flex-col items-center gap-2">
            <div class="w-10 h-10 rounded-xl bg-slate-100 dark:bg-[#18243A] text-slate-400 flex items-center justify-center">
              <FileX class="w-5 h-5" />
            </div>
            <p class="font-bold text-slate-800 dark:text-slate-200 text-sm">No leaves found</p>
            <p class="text-xs text-slate-400 dark:text-slate-500">Try adjusting your filters or create a new request</p>
          </div>
        </li>
      </ul>

      <!-- Desktop table (hidden lg:block) -->
      <div
        class="hidden lg:block bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl shadow-xs border border-slate-200/90 dark:border-[#263449] overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full text-xs sm:text-sm">
            <thead class="bg-slate-50/80 dark:bg-[#18243A] text-slate-600 dark:text-slate-300 border-b border-slate-200/80 dark:border-[#263449]">
              <tr class="text-left font-semibold">
                <th class="px-5 py-3 w-12 text-center">#</th>
                <th class="px-5 py-3">Leave Type</th>
                <th class="px-5 py-3">From</th>
                <th class="px-5 py-3">To</th>
                <th class="px-5 py-3">Days</th>
                <th class="px-5 py-3">Reason</th>
                <th class="px-5 py-3">Status</th>
                <th class="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>

            <tbody class="divide-y divide-slate-100 dark:divide-[#1E293B]">
              <tr
                v-for="(leave, i) in leaves"
                :key="leave._id"
                class="hover:bg-slate-50/50 dark:hover:bg-[#152238] transition-colors"
              >
                <td class="px-5 py-3.5 font-medium text-slate-500 dark:text-slate-400 text-center">
                  {{ getRowNumber(i) }}
                </td>

                <td class="px-5 py-3.5 font-bold text-slate-900 dark:text-white">
                  {{ leave.leave_type }}
                </td>

                <td class="px-5 py-3.5 text-slate-600 dark:text-slate-300">
                  {{ formatDateTime(leave.from_datetime) }}
                  <span class="text-xs text-slate-400 ml-1">({{ leave.start_time }})</span>
                </td>

                <td class="px-5 py-3.5 text-slate-600 dark:text-slate-300">
                  {{ formatDateTime(leave.to_datetime) }}
                  <span class="text-xs text-slate-400 ml-1">({{ leave.end_time }})</span>
                </td>

                <td class="px-5 py-3.5 font-bold text-slate-900 dark:text-white">
                  {{ leave.duration_days }}
                </td>

                <td class="px-5 py-3.5 text-slate-600 dark:text-slate-300 max-w-xs truncate" :title="leave.reason">
                  {{ leave.reason }}
                </td>

                <td class="px-5 py-3.5">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border"
                    :class="badgeClass(leave.status)"
                  >
                    {{ leave.status }}
                  </span>
                </td>

                <td class="px-5 py-3.5 text-right">
                  <button
                    v-if="leave.status === 'Pending'"
                    @click="cancelLeaveRequest(leave._id)"
                    :disabled="loading"
                    class="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:text-rose-700 hover:underline disabled:opacity-50 transition-colors"
                  >
                    <Trash2 class="w-3.5 h-3.5" />
                    Cancel
                  </button>
                  <span v-else class="text-xs text-slate-400 dark:text-slate-600">—</span>
                </td>
              </tr>

              <tr v-if="leaves.length === 0 && !loading">
                <td colspan="8" class="text-center py-12 text-slate-400 dark:text-slate-500 text-xs sm:text-sm">
                  No leave applications found. Click "New Request" to submit one.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          v-if="pagination.totalPages > 1"
          class="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-[#1E293B] bg-slate-50/50 dark:bg-[#18243A]/40"
        >
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-50 transition-colors shadow-xs"
            :disabled="pagination.page === 1 || loading"
            @click="changePage(pagination.page - 1)"
          >
            <ChevronLeft class="w-3.5 h-3.5" /> Previous
          </button>

          <div class="text-xs text-slate-500 dark:text-slate-400 font-medium">
            Page <span class="font-bold text-slate-900 dark:text-white">{{ pagination.page }}</span> of <span class="font-bold text-slate-900 dark:text-white">{{ pagination.totalPages }}</span>
          </div>

          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-50 transition-colors shadow-xs"
            :disabled="pagination.page === pagination.totalPages || loading"
            @click="changePage(pagination.page + 1)"
          >
            Next <ChevronRight class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Apply Leave Modal (matching EditStudentModal design system) -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[1005] flex items-center justify-center bg-black/50 backdrop-blur-xs p-3 sm:p-4"
      @click.self="closeModal"
    >
      <div
        class="w-full max-w-xl bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-slate-200/90 dark:border-[#263449] max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200"
      >
        <!-- Header -->
        <div class="px-5 py-4 border-b border-slate-100 dark:border-[#1E293B] flex items-center justify-between">
          <div>
            <h3 class="text-base font-bold text-slate-900 dark:text-white">New Leave Request</h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Submit your leave application for administrative review</p>
          </div>
          <button
            class="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-[#18243A] transition-colors"
            @click="closeModal"
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        <!-- Form Body -->
        <form
          @submit.prevent="submitLeaveRequest"
          novalidate
          class="flex flex-col flex-1 overflow-y-auto"
        >
          <div class="p-5 space-y-4">
            <!-- Leave Type -->
            <div class="space-y-1">
              <label
                for="type"
                class="block text-xs font-semibold text-slate-700 dark:text-slate-300"
              >
                Leave Type <span class="text-rose-500">*</span>
              </label>

              <div class="relative">
                <select
                  id="type"
                  v-model="form.leaveType"
                  :class="[
                    'w-full rounded-xl border bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 outline-none transition-all appearance-none',
                    errors.leaveType
                      ? 'border-rose-400 focus:ring-1 focus:ring-rose-500'
                      : 'border-slate-200/80 dark:border-[#263449] focus:ring-1 focus:ring-indigo-500',
                  ]"
                  required
                >
                  <option value="">Select leave type</option>
                  <option
                    v-for="item in leaveBalance?.balances || []"
                    :key="item.type"
                    :value="item.type"
                    :disabled="userRole !== 'admin' && item.remaining <= 0"
                  >
                    {{ item.type }}
                    <span v-if="userRole !== 'admin'">
                      ({{ item.remaining }} left)
                    </span>
                  </option>
                </select>
                <ChevronDown
                  class="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>

              <!-- Remaining Balance Info -->
              <div
                v-if="userRole !== 'admin' && form.leaveType"
                class="text-[11px] mt-1 flex items-center justify-between"
              >
                <span class="text-slate-500 dark:text-slate-400">Available:</span>
                <span
                  :class="[
                    'font-bold',
                    selectedLeaveRemaining <= 2
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-emerald-600 dark:text-emerald-400',
                  ]"
                >
                  {{ selectedLeaveRemaining }} day(s)
                </span>
              </div>

              <p
                v-if="errors.leaveType"
                class="text-xs text-rose-600 flex items-center gap-1 mt-1"
              >
                <AlertCircle class="w-3.5 h-3.5 shrink-0" />
                {{ errors.leaveType }}
              </p>
            </div>

            <!-- From Date & Duration -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="space-y-1">
                <label
                  for="fromDate"
                  class="block text-xs font-semibold text-slate-700 dark:text-slate-300"
                >
                  From Date <span class="text-rose-500">*</span>
                </label>
                <input
                  id="fromDate"
                  v-model="form.fromDate"
                  type="date"
                  class="w-full rounded-xl border border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                  required
                />
                <p
                  v-if="errors.fromDate"
                  class="text-xs text-rose-600 flex items-center gap-1 mt-0.5"
                >
                  <AlertCircle class="w-3.5 h-3.5 shrink-0" />
                  {{ errors.fromDate }}
                </p>
              </div>

              <div class="space-y-1">
                <label
                  for="fromDuration"
                  class="block text-xs font-semibold text-slate-700 dark:text-slate-300"
                >
                  From Duration <span class="text-rose-500">*</span>
                </label>
                <div class="relative">
                  <select
                    id="fromDuration"
                    v-model="form.fromDuration"
                    class="w-full rounded-xl border border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 transition-all appearance-none"
                    required
                  >
                    <option value="first half" disabled>First Half</option>
                    <option value="second half">Second Half</option>
                    <option value="full day">Full Day</option>
                  </select>
                  <ChevronDown
                    class="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>

            <!-- To Date & Duration -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="space-y-1">
                <label
                  for="toDate"
                  class="block text-xs font-semibold text-slate-700 dark:text-slate-300"
                >
                  To Date <span class="text-rose-500">*</span>
                </label>
                <input
                  id="toDate"
                  v-model="form.toDate"
                  type="date"
                  :min="form.fromDate || ''"
                  class="w-full rounded-xl border border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                  required
                />
                <p
                  v-if="errors.toDate"
                  class="text-xs text-rose-600 flex items-center gap-1 mt-0.5"
                >
                  <AlertCircle class="w-3.5 h-3.5 shrink-0" />
                  {{ errors.toDate }}
                </p>
              </div>

              <div class="space-y-1">
                <label
                  for="toDuration"
                  class="block text-xs font-semibold text-slate-700 dark:text-slate-300"
                >
                  To Duration <span class="text-rose-500">*</span>
                </label>
                <div class="relative">
                  <select
                    id="toDuration"
                    v-model="form.toDuration"
                    class="w-full rounded-xl border border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 transition-all appearance-none"
                    required
                  >
                    <option value="first half">First Half</option>
                    <option value="second half" disabled>Second Half</option>
                    <option value="full day">Full Day</option>
                  </select>
                  <ChevronDown
                    class="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>

            <!-- Total Days pill -->
            <div
              v-if="totalLeaveDays"
              class="flex items-center justify-between text-xs bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 rounded-xl px-3 py-2 text-indigo-700 dark:text-indigo-300 font-semibold"
            >
              <span>Calculated Leave Duration:</span>
              <span class="font-bold">{{ totalLeaveDays }}</span>
            </div>

            <!-- Reason -->
            <div class="space-y-1">
              <label
                for="reason"
                class="block text-xs font-semibold text-slate-700 dark:text-slate-300"
              >
                Reason <span class="text-rose-500">*</span>
              </label>
              <textarea
                id="reason"
                v-model="form.reason"
                rows="3"
                placeholder="Please state the purpose of your leave request..."
                class="w-full rounded-xl border border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 transition-all resize-none"
                required
              ></textarea>
              <div class="flex justify-between items-center text-[11px]">
                <p
                  v-if="errors.reason"
                  class="text-rose-600 flex items-center gap-1"
                >
                  <AlertCircle class="w-3.5 h-3.5 shrink-0" />
                  {{ errors.reason }}
                </p>
                <p class="text-slate-400 dark:text-slate-500 ml-auto">
                  {{ form.reason.length }}/500
                </p>
              </div>
            </div>

            <!-- Submit Error Alert -->
            <p
              v-if="submitError"
              class="text-xs text-rose-600 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/40 rounded-xl p-3 flex items-center gap-2"
            >
              <AlertCircle class="w-4 h-4 shrink-0" />
              {{ submitError }}
            </p>
          </div>

          <!-- Modal Footer -->
          <div
            class="flex items-center justify-end gap-2.5 border-t border-slate-100 dark:border-[#1E293B] px-5 py-3.5 bg-slate-50/50 dark:bg-[#18243A]/40 mt-auto"
          >
            <button
              type="button"
              class="px-4 py-2 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30] text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A] transition-colors shadow-xs"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs font-semibold text-white disabled:opacity-50 transition-all shadow-xs flex items-center gap-1.5"
              :disabled="loading"
            >
              <Send class="w-3.5 h-3.5" />
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import {
    Clock,
    Filter,
    Search,
    Plus,
    CheckCircle,
    CheckCircle2,
    XCircle,
    AlertCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    MoreVertical,
    X,
    Trash2,
    FileX,
    FileText,
    Send,
    RotateCcw,
  } from "lucide-vue-next";

  const store = useStore();
  const userRole = computed(() => store.getters.getUserRole);
  const leaveBalance = computed(() => store.getters.getLeaveBalance);

  // Reactive state
  const loading = ref(false);
  const loadingMessage = ref("");
  const modalOpen = ref(false);
  const showMobileFilters = ref(false);
  const rowMenuId = ref(null);
  const submitError = ref("");

  // Toast notification system
  const toasts = ref([]);

  // Leave types
  const leaveTypes = computed(
    () => leaveBalance.value?.balances?.map((b) => b.type) || [],
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Available years for year selector
  const availableYears = computed(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  });

  // Form data
  const form = reactive({
    leaveType: "",
    fromDate: "",
    fromDuration: "full day",
    toDate: "",
    toDuration: "full day",
    reason: "",
  });

  // Form errors
  const errors = reactive({
    leaveType: "",
    fromDate: "",
    fromDuration: "",
    toDate: "",
    toDuration: "",
    reason: "",
  });

  // Filters
  const filters = reactive({
    search: "",
    leave_type: "",
    month: "",
    year: new Date().getFullYear(),
  });

  // Data from store
  const leaves = computed(() => store.state.leaveRequests || []);

  const leaveSummary = computed(
    () =>
      store.state.leaveSummary || {
        totalLeaves: 0,
        totalApproved: 0,
        totalPending: 0,
        totalDeclined: 0,
      },
  );

  // Pagination
  const pagination = ref({
    page: 1,
    limit: 20,
    totalPages: 1,
    total: 0,
  });

  const getTotalLeaves = computed(
    () =>
      leaveSummary.value.totalLeaves !== undefined &&
      leaveSummary.value.totalLeaves !== null
        ? leaveSummary.value.totalLeaves
        : leaves.value.length,
  );
  const getTotalApproved = computed(
    () => leaveSummary.value.totalApproved || 0,
  );
  const getTotalPending = computed(() => leaveSummary.value.totalPending || 0);
  const getTotalDeclined = computed(
    () => leaveSummary.value.totalDeclined || 0,
  );

  const getFilterButtonText = computed(() =>
    loading.value ? "Filtering..." : "Apply",
  );

  function getToastClass(type) {
    return type === "success"
      ? "border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/90 text-emerald-900 dark:text-emerald-100"
      : "border-rose-200 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/90 text-rose-900 dark:text-rose-100";
  }

  function getToastIconClass(type) {
    return type === "success"
      ? "border-emerald-200 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400"
      : "border-rose-200 dark:border-rose-700 text-rose-600 dark:text-rose-400";
  }

  function getToastTitleClass(type) {
    return type === "success" ? "text-emerald-800 dark:text-emerald-200" : "text-rose-800 dark:text-rose-200";
  }

  function getToastMessageClass(type) {
    return type === "success" ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300";
  }

  function getToastButtonClass(type) {
    return type === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-rose-600 dark:text-rose-400";
  }

  function getToastTitle(type) {
    return type === "success" ? "Success" : "Error";
  }

  function getRowNumber(index) {
    return (pagination.value.page - 1) * pagination.value.limit + index + 1;
  }

  // Toast notification methods
  function showToast(message, type = "success") {
    const id = Date.now();
    toasts.value.push({ id, message, type });

    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }

  function removeToast(id) {
    const index = toasts.value.findIndex((toast) => toast.id === id);
    if (index > -1) {
      toasts.value.splice(index, 1);
    }
  }

  // Methods
  async function fetchLeaves() {
    try {
      loading.value = true;
      loadingMessage.value = "Loading leaves...";

      const params = {
        page: pagination.value.page,
        limit: pagination.value.limit,
        search: filters.search,
        leave_type: filters.leave_type,
        month: filters.month,
        year: filters.year,
        view: "true",
      };

      const result = await store.dispatch("fetchMyLeaves", params);

      if (result && result.pagination) {
        pagination.value = {
          page: result.pagination.page || 1,
          limit: result.pagination.limit || 10,
          totalPages: result.pagination.totalPages || 1,
          total: result.pagination.total || 0,
        };
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || err.message || "Failed to fetch leaves",
        "error",
      );
    } finally {
      loading.value = false;
      loadingMessage.value = "";
    }
  }

  async function submitLeaveRequest() {
    if (!validateForm()) {
      showToast("Please fix the errors in the form.", "error");
      return;
    }

    try {
      loading.value = true;
      loadingMessage.value = "Submitting leave request...";

      const fromDate = new Date(form.fromDate);
      const toDate = new Date(form.toDate);
      let days = (toDate - fromDate) / (1000 * 60 * 60 * 24) + 1;
      if (days < 0) days = 0;

      let totalDuration = days;
      if (
        form.fromDuration === "first half" ||
        form.fromDuration === "second half"
      ) {
        totalDuration -= 0.5;
      }
      if (
        form.toDuration === "first half" ||
        form.toDuration === "second half"
      ) {
        totalDuration -= 0.5;
      }

      const leaveData = {
        leaveType: form.leaveType,
        fromDate: form.fromDate,
        fromDuration: form.fromDuration,
        toDate: form.toDate,
        toDuration: form.toDuration,
        reason: form.reason.trim(),
        duration_days: totalDuration,
      };

      const selectedType = leaveBalance.value?.balances?.find(
        (b) => b.type === form.leaveType,
      );

      if (!selectedType) {
        showToast("Leave type not configured.", "error");
        return;
      }

      if (selectedType.remaining <= 0) {
        showToast(`No ${form.leaveType} leaves remaining.`, "error");
        return;
      }
      await store.dispatch("applyLeave", leaveData);

      showToast("Leave request submitted successfully!");

      closeModal();
      resetForm();
      await fetchLeaves();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to submit leave request";

      submitError.value = message;
      showToast(message, "error");
    } finally {
      loading.value = false;
      loadingMessage.value = "";
    }
  }

  async function cancelLeaveRequest(leaveId) {
    if (!confirm("Are you sure you want to cancel this leave request?")) {
      return;
    }

    try {
      loading.value = true;
      loadingMessage.value = "Cancelling leave request...";

      await store.dispatch("cancelLeave", leaveId);

      showToast("Leave request cancelled successfully!");

      rowMenuId.value = null;
      await fetchLeaves();
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          err.message ||
          "Failed to cancel leave request",
        "error",
      );
    } finally {
      loading.value = false;
      loadingMessage.value = "";
    }
  }

  function validateForm() {
    Object.keys(errors).forEach((key) => (errors[key] = ""));
    let isValid = true;

    if (!form.leaveType) {
      errors.leaveType = "Please select a leave type.";
      isValid = false;
    }

    if (!form.fromDate) {
      errors.fromDate = "Please select the start date.";
      isValid = false;
    }

    if (!form.toDate) {
      errors.toDate = "Please select the end date.";
      isValid = false;
    } else if (
      form.fromDate &&
      new Date(form.toDate) < new Date(form.fromDate)
    ) {
      errors.toDate = "End date cannot be before start date.";
      isValid = false;
    }

    const reason = form.reason.trim();
    if (!reason) {
      errors.reason = "Please enter a reason for your leave.";
      isValid = false;
    } else if (reason.length < 6) {
      errors.reason = "Reason must be at least 6 characters.";
      isValid = false;
    } else if (reason.length > 500) {
      errors.reason = "Reason cannot exceed 500 characters.";
      isValid = false;
    }

    return isValid;
  }

  function resetForm() {
    form.leaveType = "";
    form.fromDate = "";
    form.fromDuration = "full day";
    form.toDate = "";
    form.toDuration = "full day";
    form.reason = "";

    Object.keys(errors).forEach((key) => (errors[key] = ""));
  }

  function clearFilters() {
    filters.search = "";
    filters.leave_type = "";
    filters.month = "";
    filters.year = new Date().getFullYear();
    pagination.value.page = 1;
    fetchLeaves();
  }

  function changePage(page) {
    pagination.value.page = page;
    fetchLeaves();
  }

  function openModal() {
    modalOpen.value = true;
    rowMenuId.value = null;
    submitError.value = "";
  }

  function closeModal() {
    modalOpen.value = false;
    resetForm();
    submitError.value = "";
  }

  function toggleRowMenu(id) {
    rowMenuId.value = rowMenuId.value === id ? null : id;
  }

  function badgeClass(status) {
    switch (status) {
      case "Approved":
        return "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200/70 dark:border-emerald-800/40";
      case "Declined":
      case "Rejected":
        return "text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/50 border-rose-200/70 dark:border-rose-800/40";
      case "Cancelled":
        return "text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-[#18243A] border-slate-200 dark:border-[#263449]";
      default:
        return "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border-amber-200/70 dark:border-amber-800/40";
    }
  }

  function formatDateTime(dateString) {
    try {
      const date = new Date(dateString);
      const options = { year: "numeric", month: "short", day: "2-digit" };
      return date.toLocaleDateString(undefined, options);
    } catch {
      return "";
    }
  }

  watch(
    () => filters.search,
    () => {
      pagination.value.page = 1;
    },
  );

  watch(
    () => filters.leave_type,
    () => {
      pagination.value.page = 1;
    },
  );

  watch(
    () => filters.month,
    () => {
      pagination.value.page = 1;
    },
  );

  watch(
    () => filters.year,
    () => {
      pagination.value.page = 1;
    },
  );

  const totalLeaveDays = computed(() => {
    if (!form.fromDate || !form.toDate) return "";

    const fromDate = new Date(form.fromDate);
    const toDate = new Date(form.toDate);

    let days = (toDate - fromDate) / (1000 * 60 * 60 * 24) + 1;
    if (days < 0) return "";

    let total = days;

    if (
      form.fromDuration === "first half" ||
      form.fromDuration === "second half"
    ) {
      total -= 0.5;
    }

    if (form.toDuration === "first half" || form.toDuration === "second half") {
      total -= 0.5;
    }

    if (total <= 0) return "Half day";

    const integerPart = Math.floor(total);
    const fractionalPart = total - integerPart;

    if (fractionalPart >= 0.5) {
      return `${integerPart} /half day`;
    } else {
      return `${integerPart} day${integerPart > 1 ? "s" : ""}`;
    }
  });

  const selectedLeaveRemaining = computed(() => {
    const selected = leaveBalance.value?.balances?.find(
      (b) => b.type === form.leaveType,
    );
    return selected?.remaining ?? 0;
  });

  watch(
    () => ({ ...form }),
    () => {
      submitError.value = "";
    },
    { deep: true },
  );

  onMounted(() => {
    fetchLeaves();
    store.dispatch("fetchMyLeaveBalance");
  });
</script>

<style scoped>
  table td {
    white-space: nowrap;
    text-overflow: ellipsis;
  }
</style>
